import { ERA_LABELS } from './historicSitesData.js';
import { OBSERVATIONS } from './learningData.js';
import { element as el, button, briefSummary, speechButton, sourceLinks } from './learningWidgets.js';
import { narration } from './narration.js';

// Owns only the dialog. Flight pause and durable progress remain with the caller.
export function createSiteModal({ onResume, onAnswer, onObservation, onReviewRequest, isReviewRequested = () => false } = {}) {
  const get = id => document.getElementById(id);
  const root = get('site-modal');
  if (!root) return { open() {}, close() {}, isOpen: () => false, dispose() {} };
  const scroll = get('site-scroll');
  const information = get('site-information');
  const observationRoot = get('site-observation');
  const reviewBtn = get('site-review');
  const trueBtn = get('quiz-true'), falseBtn = get('quiz-false'), resumeBtn = get('site-resume');
  const gameRoot = get('game-root');
  let current = null, answered = false, previousFocus = null, observationAnswer = null;

  function syncReview() {
    const requested = current && isReviewRequested(current.id);
    reviewBtn.textContent = requested ? '✓ 復習に追加済み' : 'あとで復習';
    reviewBtn.setAttribute('aria-pressed', String(!!requested));
  }
  function showInformation() {
    narration.stop();
    observationRoot.hidden = true;
    information.hidden = false;
    root.setAttribute('aria-describedby', 'site-summary');
    scroll.scrollTop = 0;
    scroll.focus({ preventScroll: true });
  }
  function renderObservation(site) {
    const observation = !site.solemn && OBSERVATIONS[site.id];
    observationRoot.replaceChildren();
    observationRoot.hidden = !observation;
    information.hidden = !!observation;
    observationAnswer = null;
    if (!observation) return;
    root.setAttribute('aria-describedby', 'site-observation');
    observationRoot.append(el('p', '空から観察 · 考えてみよう', 'eyebrow'), el('h3', observation.prompt));
    const hint = el('p', observation.hint, 'observation-hint'); hint.hidden = true;
    const hintBtn = button('ヒントを見る', () => { hint.hidden = !hint.hidden; hintBtn.setAttribute('aria-expanded', String(!hint.hidden)); });
    hintBtn.setAttribute('aria-expanded', 'false');
    observationRoot.append(hintBtn, hint, speechButton(`question-${site.id}`, observation.prompt));
    const choices = el('div', undefined, 'observation-choices');
    const result = el('div', undefined, 'observation-result'); result.setAttribute('role', 'status');
    const proceed = button('解説を読む（解答を見送る）', showInformation, 'primary-btn');
    let resolved = false;
    observationAnswer = choice => {
      if (resolved) return;
      resolved = true;
      const correct = choice === observation.answer;
      for (const [i, b] of [...choices.children].entries()) { b.disabled = true; if (i === observation.answer) b.classList.add('correct'); }
      result.append(el('h4', correct ? 'その通り！' : 'こんな見方ができるよ'), el('p', observation.explain));
      proceed.textContent = '詳しい解説へ';
      onObservation?.(site.id, correct);
      proceed.focus({ preventScroll: true });
      result.scrollIntoView({ block: 'nearest' });
    };
    observation.choices.forEach((text, i) => choices.append(button(`${i + 1}. ${text}`, () => observationAnswer(i), 'observation-choice')));
    observationRoot.append(choices, result, proceed, el('p', '景色をもう一度見たいときは飛行を再開。正誤にかかわらず旅を続けられます。', 'learning-status'));
  }
  function answer(choice) {
    if (!current?.quiz || answered || information.hidden) return;
    answered = true;
    const correct = choice === current.quiz.answer;
    trueBtn.disabled = falseBtn.disabled = true;
    (current.quiz.answer ? trueBtn : falseBtn).classList.add('correct');
    if (!correct) (choice ? trueBtn : falseBtn).classList.add('wrong');
    get('site-quiz-verdict').textContent = correct ? '正解！' : '理由を確かめよう';
    get('site-quiz-verdict').className = 'quiz-verdict ' + (correct ? 'correct' : 'wrong');
    get('site-quiz-explain').textContent = current.quiz.explain;
    get('site-quiz-result').hidden = false;
    onAnswer?.(current.id, correct);
    syncReview();
    get('site-quiz-result').scrollIntoView({ block: 'nearest' });
    resumeBtn.focus({ preventScroll: true });
  }
  function close({ listenInFlight = false } = {}) {
    if (!current) return;
    narration.stop();
    current = null;
    root.hidden = true;
    gameRoot?.classList.remove('modal-open');
    previousFocus?.focus?.({ preventScroll: true });
    previousFocus = null;
    onResume?.({ listenInFlight });
  }
  function onKeyDown(e) {
    if (!current) return;
    if (e.key === 'Tab') {
      const nodes = [...root.querySelectorAll('button:not(:disabled), a[href], input, select, textarea, summary, [tabindex="0"]')].filter(n => n.getClientRects().length);
      if (!nodes.length) return;
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if (e.target.closest?.('input, select, textarea, a, summary')) return;
    // Escape has one path: main -> game.handleEscape -> close.
    if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') { e.preventDefault(); close(); return; }
    if (!observationRoot.hidden && /^[123]$/.test(e.key)) { e.preventDefault(); observationAnswer?.(Number(e.key) - 1); return; }
    if (['1', 'o', 'O'].includes(e.key)) { e.preventDefault(); answer(true); }
    if (['2', 'x', 'X'].includes(e.key)) { e.preventDefault(); answer(false); }
  }
  const onTrue = () => answer(true), onFalse = () => answer(false), onResumeClick = () => close();
  const onReview = () => { if (current) { onReviewRequest?.(current.id, !isReviewRequested(current.id)); syncReview(); } };
  trueBtn.addEventListener('click', onTrue); falseBtn.addEventListener('click', onFalse);
  resumeBtn.addEventListener('click', onResumeClick); reviewBtn.addEventListener('click', onReview);
  window.addEventListener('keydown', onKeyDown);

  return {
    open(site, { visitedCount = 0, totalCount = 0, alreadyVisited = false, courseLabel = '' } = {}) {
      if (current) return;
      current = site; answered = false; previousFocus = document.activeElement;
      get('site-era').textContent = ERA_LABELS[site.era]; get('site-era').className = `site-badge era-${site.era}`;
      get('site-place').textContent = `${site.emoji} ${site.country}`;
      get('site-name').textContent = site.name; get('site-name-en').textContent = site.nameEn;
      get('site-progress').textContent = courseLabel || `訪問 ${visitedCount} / ${totalCount} · ${alreadyVisited ? '訪問済み' : site.solemn ? '初訪問' : '新しい発見'}`;
      get('site-summary').textContent = briefSummary(site);
      get('site-full-summary').textContent = site.summary;
      get('site-details').open = false;
      const facts = get('site-facts'); facts.replaceChildren();
      for (const { label, value } of [{ label: '年代', value: site.yearLabel }, { label: '国・地域', value: site.country }, { label: '文明・王朝', value: site.civilization }, { label: '世界遺産', value: site.whcYear ? `${site.whcYear}年 登録 ${site.whcNote ?? ''}` : '—' }, ...(site.facts ?? [])]) {
        const row = el('div', undefined, 'fact-row'); row.append(el('dt', label), el('dd', value)); facts.append(row);
      }
      get('site-sources').replaceChildren(sourceLinks(site));
      const tools = get('site-reading-tools');
      const listen = button('聴きながら飛ぶ', () => {
        const site = current;
        close({ listenInFlight: true });
        narration.speak(site.id, `${site.name}。${site.summary}`);
      });
      listen.disabled = !narration.supported;
      const speechStatus = el('p', narration.message, 'learning-status'); speechStatus.dataset.speechStatus = ''; speechStatus.setAttribute('role', 'status');
      tools.replaceChildren(speechButton(site.id, `${site.name}。${site.summary}`), listen, speechStatus);
      get('site-quiz').hidden = !site.quiz || !!site.solemn;
      get('site-quiz-statement').textContent = site.quiz?.statement ?? '';
      get('site-quiz-result').hidden = true;
      get('site-quiz-verdict').textContent = ''; get('site-quiz-explain').textContent = '';
      for (const b of [trueBtn, falseBtn]) { b.disabled = false; b.className = 'quiz-btn'; }
      reviewBtn.hidden = !site.quiz || !!site.solemn; syncReview();
      root.setAttribute('aria-describedby', 'site-summary');
      renderObservation(site);
      root.hidden = false;
      gameRoot?.classList.add('modal-open');
      scroll.scrollTop = 0; scroll.focus({ preventScroll: true });
    },
    close,
    isOpen: () => !!current,
    dispose() {
      narration.stop();
      window.removeEventListener('keydown', onKeyDown);
      trueBtn.removeEventListener('click', onTrue); falseBtn.removeEventListener('click', onFalse);
      resumeBtn.removeEventListener('click', onResumeClick); reviewBtn.removeEventListener('click', onReview);
      current = null; root.hidden = true; gameRoot?.classList.remove('modal-open');
    },
  };
}
